import { Vector3 } from 'three';

import { Component, Member } from '@paragon/contract-library';
import { distanceGreaterThanOrEqual, LineSegment2D, Point2D } from '@paragon/geometry-library';
import { calculateMemberMidline, calculateMemberOverallLengthSegment, Segment3D } from '@paragon/three-shared';
import { notNullish } from '@paragon/utilities';

export default function getDistanceDimensionDefinitionsForMember(member: Member, component: Component) {
  const { midline, overallLengthSegment } = getUniqueDimensionsForMember(member, component);

  return [
    midline ? getOffsetDistanceDimensionDefinition(midline, 1) : null,
    overallLengthSegment ? getOffsetDistanceDimensionDefinition(overallLengthSegment, -1) : null,
  ].filter(notNullish);
}

// Returns only the midline if the midline and overall length segment are the same.
function getUniqueDimensionsForMember(member: Member, component: Component) {
  const midline = calculateMemberMidline(member, component);
  const overallLengthSegment = calculateMemberOverallLengthSegment(member, component);

  if (!midline || !overallLengthSegment) return { midline, overallLengthSegment };

  const midlineGeometrySegment = new LineSegment2D(Point2D.fromObject(midline.start), Point2D.fromObject(midline.end));
  const overallLengthGeometrySegment = new LineSegment2D(
    Point2D.fromObject(overallLengthSegment.start),
    Point2D.fromObject(overallLengthSegment.end),
  );

  return midlineGeometrySegment.equals(overallLengthGeometrySegment)
    ? { midline, overallLengthSegment: null }
    : { midline, overallLengthSegment };
}

export function getOffsetDistanceDimensionDefinition(undirectionedSegment: Segment3D, sign: number) {
  const segment = makeSegmentLeftToRight(undirectionedSegment);

  const direction = new Vector3(segment.end.x, segment.end.y, segment.end.z).sub(
    new Vector3(segment.start.x, segment.start.y, segment.start.z),
  );
  direction.normalize();
  direction.applyAxisAngle(new Vector3(0, 0, 1), sign * Math.PI * 0.5);
  direction.multiplyScalar(3);
  const start = new Vector3(segment.start.x, segment.start.y, segment.start.z);
  const end = new Vector3(segment.end.x, segment.end.y, segment.end.z);
  start.add(direction);
  end.add(direction);

  return { start, end, textFloat: sign * 4 };
}

function makeSegmentLeftToRight(segment: Segment3D) {
  return distanceGreaterThanOrEqual(segment.end.x, segment.start.x)
    ? segment
    : { start: segment.end, end: segment.start };
}
